import { faChevronRight, faCircleInfo } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Page } from '~/components/Page'
import { SeasonTierBadge } from '~/features/leaderboard/components/SeasonTierBadge'
import { useKillRaceActive } from '../api/useKillRaceActive'
import { Countdown } from '../components/Countdown'
import { KillRaceBrowser } from '../components/KillRaceBrowser'
import { ACCENT, Panel, Skeleton } from '../components/KillRaceUI'
import { KillRace as KillRaceType } from '../types'
import { resolvePhase, secondsUntil } from '../utils/killrace'

const title = new URL('../assets/killrace_title_2.svg', import.meta.url).href
const bg = new URL('../assets/br1_arena_bg.png', import.meta.url).href
const stopwatch = new URL('../assets/stopwatch_icon.svg', import.meta.url).href

/**
 * Tournaments.
 *
 * Three bands, down from four, and the middle one carries its own navigation:
 *
 *   HERO      wordmark · one-line pitch · season tier      |  the clock that matters
 *   BROWSER   [ LIVE n ] [ UPCOMING n ] [ RESULTS n ]  →  one grid
 *   HOW       ‹ disclosure, closed ›
 *
 * What changed and why, measured at 375×812 against the version this replaces:
 *
 * - The page was 4135px — 5.1 screens — and the first race card did not appear until 773px. It is
 *   now roughly half that, and the first card is above the fold.
 * - `Live now` and `Upcoming` were separate sections, each with its own two-column grid. One race
 *   in each meant two half-width cards stacked vertically beside an empty column on a wide screen.
 *   One grid behind a tab bar fills the row and drops a heading.
 * - `How kill races work` was 619px of permanent prose on a phone — 15% of the page — for
 *   three paragraphs a returning player reads once. It is a disclosure now, the same call the
 *   Inventory page's rate table made.
 * - The hero's three-stat strip is gone: the tab labels carry those numbers, and reading them off
 *   the control you are about to press beats reading them 600px above it.
 */
export const KillRace = () => (
    <Page title='Kill Race' header='Kill Race'>
        {/* `pt` is the fix for the panel's notched border butting straight into the promo banner
            and the nav above it — the container had no top gutter at any width. */}
        <div className='mx-auto mb-16 flex w-full max-w-[1400px] flex-col gap-6 px-4 pt-5 md:mb-24 md:gap-8 md:pt-8'>
            <Hero />
            <KillRaceBrowser />
            <HowItWorks />
        </div>
    </Page>
)

/**
 * Hero. Reads from the same `useKillRaceActive` query the browser below uses — React Query dedupes
 * by key, so this is one request, not two.
 */
const Hero = () => {
    const { data, isLoading } = useKillRaceActive()
    const races: KillRaceType[] = Array.isArray(data) ? data : []
    const live = races.filter((race) => resolvePhase(race.phase, race.begin, race.end) === 'Live')

    // A live race's own end is the more useful clock when something is running; otherwise the
    // soonest start is the next thing that will happen to the player.
    const soonestEnd = live
        .map((race) => secondsUntil(race.end))
        .filter((seconds): seconds is number => seconds !== null)
        .sort((a, b) => a - b)[0]

    const nextStart = races
        .filter((race) => resolvePhase(race.phase, race.begin, race.end) === 'Upcoming')
        .map((race) => secondsUntil(race.begin))
        .filter((seconds): seconds is number => seconds !== null)
        .sort((a, b) => a - b)[0]

    const seconds = soonestEnd ?? nextStart

    return (
        <Panel size={20} border={ACCENT}>
            <div
                // `md:gap-6 lg:gap-10`: at 768 the row is sharing its width with the countdown
                // block, and the full gap left the season badge two pixels short of its own name.
                className='relative flex flex-col items-center gap-5 px-5 py-6 text-center md:flex-row md:items-center md:justify-between md:gap-6 md:px-10 md:py-8 md:text-left lg:gap-10'
                style={{ backgroundImage: `url(${bg})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
            >
                {/* Lighter than the near-opaque wash this had: the arena art was paid for in
                    bytes and then hidden under it. On md the fade runs sideways so the artwork
                    survives on the right, behind the clock. */}
                <div className='absolute inset-0 bg-gradient-to-b from-black/90 to-black/70 md:bg-gradient-to-r md:from-black md:via-black/85 md:to-black/40' />

                {/* `w-full` / `min-w-0` are load-bearing, not tidying. This column is shrink-to-fit
                    by default, so a `max-w-full` child resolves 100% against a parent that has
                    already grown to fit it — no cap at all. The season badge is free text and blew
                    past the panel edge, where `Panel`'s clip-path silently sliced it rather than
                    overflowing visibly. Giving the column a definite width on mobile, and letting
                    it shrink in the row from md, is what makes the child's cap mean anything. */}
                <div className='relative flex w-full min-w-0 flex-col items-center gap-3 md:w-auto md:items-start'>
                    <img src={title} alt='Kill Race' className='w-full max-w-[240px] md:max-w-[420px]' />
                    <p className='max-w-md text-xs font-medium leading-relaxed text-white/55 md:text-sm'>
                        Free-to-enter tournaments running alongside live matches. Rack up kills, climb the ladder,
                        take a cut of the pool.
                    </p>
                    <SeasonTierBadge />
                </div>

                <div className='relative flex shrink-0 items-center gap-3 border border-white/15 bg-black/60 px-4 py-3 backdrop-blur-sm'>
                    <img src={stopwatch} alt='' className='h-5 w-5 shrink-0 opacity-60' />
                    <div className='flex flex-col gap-1 text-left'>
                        <span className='text-2xs uppercase leading-none tracking-[0.2em] text-white/45'>
                            {soonestEnd !== undefined ? 'Next race ends in' : 'Next race starts in'}
                        </span>
                        <span className='text-lg font-black leading-none tabular-nums md:text-xl'>
                            {isLoading ? (
                                <Skeleton className='h-5 w-32' />
                            ) : seconds !== undefined ? (
                                <Countdown seconds={seconds} />
                            ) : (
                                'Not scheduled'
                            )}
                        </span>
                    </div>
                </div>
            </div>
        </Panel>
    )
}

const STEPS = [
    {
        title: 'Play to enter',
        body: 'Get a kill while a race is live and you are entered automatically. No sign-up, no entry fee, no separate queue.',
    },
    {
        title: 'Climb the ladder',
        body: 'Ranked on kills first. Ties break in favour of fewer deaths, then more sessions played.',
    },
    {
        title: 'Get paid',
        body: 'Finish inside the paid places and your share is credited to your BR1 balance once the race ends.',
    },
]

/**
 * Closed by default. Three paragraphs of rules are worth having and worth reading once; they are
 * not worth a screen of every visit, which is what they cost as a permanent band.
 */
const HowItWorks = () => (
    <details className='group border border-white/10 bg-white/[0.02] open:bg-white/[0.03]'>
        {/* `list-none` alone leaves WebKit's disclosure triangle in place, which lands next to our
            own chevron and reads as two controls. */}
        <summary className='flex min-h-[2.75rem] cursor-pointer select-none list-none items-center gap-3 px-4 py-3 text-xs font-black uppercase tracking-[0.18em] text-white/70 transition-colors hover:text-white [&::-webkit-details-marker]:hidden'>
            <FontAwesomeIcon icon={faCircleInfo} className='text-[#03CEFF]' />
            How kill races work
            <FontAwesomeIcon
                icon={faChevronRight}
                className='ml-auto text-2xs text-white/40 transition-transform group-open:rotate-90'
            />
        </summary>
        <div className='grid grid-cols-1 gap-4 border-t border-white/10 px-4 py-4 md:grid-cols-3 md:gap-6 md:px-5'>
            {STEPS.map((step, i) => (
                <div key={step.title} className='flex flex-col gap-2'>
                    <span className='text-xl font-black leading-none text-white/15 tabular-nums'>
                        {String(i + 1).padStart(2, '0')}
                    </span>
                    <h3 className='text-xs font-black uppercase tracking-[0.18em]'>{step.title}</h3>
                    <p className='text-sm font-medium leading-relaxed text-white/50'>{step.body}</p>
                </div>
            ))}
        </div>
    </details>
)
